/**
 * WebSocket Manager for Real-time Audio Communication
 * Handles WebSocket lifecycle, reconnection, and message management
 */

export class WebSocketManager {
  constructor(url, callbacks) {
    this.url = url;
    this.callbacks = callbacks;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimer = null;
    this.pingInterval = null;
    this.sessionId = null;
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.warn('[WebSocket] Already connected');
      return;
    }

    console.log('[WebSocket] Connecting to:', this.url);
    this.callbacks.onConnectionChange({ isConnecting: true, error: null });

    try {
      this.ws = new WebSocket(this.url);
      this.setupEventHandlers();
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error);
      this.callbacks.onConnectionChange({ 
        isConnecting: false, 
        isConnected: false, 
        error: 'Connection failed' 
      });
    }
  }

  setupEventHandlers() {
    this.ws.onopen = () => {
      console.log('[WebSocket] ✅ Connected successfully');
      this.reconnectAttempts = 0;
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      this.callbacks.onConnectionChange({ 
        isConnected: true, 
        isConnecting: false, 
        error: null 
      });

      // Initialize session with proper configuration for real-time audio
      this.initializeSession();
      this.startKeepalive();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Log raw response with full details
        console.log('═══════════════════════════════════════════════════════');
        console.log('[WebSocket] 📩 RAW RESPONSE RECEIVED');
        console.log('Timestamp:', new Date().toISOString());
        console.log('Message Type:', data.type);
        console.log('───────────────────────────────────────────────────────');
        console.log('FULL RAW DATA:');
        console.log(JSON.stringify(data, null, 2));
        console.log('═══════════════════════════════════════════════════════');
        
        this.callbacks.onMessage(data);
      } catch (error) {
        console.error('[WebSocket] ❌ Failed to parse message:', error);
        console.error('Raw event data:', event.data);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocket] ❌ Error:', error);
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocket] 🔌 Closed:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      
      this.stopKeepalive();
      
      this.callbacks.onConnectionChange({ 
        isConnected: false, 
        isConnecting: false, 
        error: event.code !== 1000 ? 'Connection lost' : null
      });

      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };
  }

  initializeSession() {
    console.log('[WebSocket] 📝 Initializing session configuration');
    
    const sessionConfig = {
      type: 'session.update',
      session: {
        modalities: ['text', 'audio'],
        voice: 'alloy',
        input_audio_format: 'pcm16',
        output_audio_format: 'pcm16',
        input_audio_transcription: {
          model: 'whisper-1'
        },
        turn_detection: {
          type: 'server_vad',
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 500
        },
        temperature: 0.8,
        max_response_output_tokens: 4096
      }
    };
    
    this.send(sessionConfig);
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const message = JSON.stringify(data);
        
        // OPTIMIZED: Simplified logging for audio messages to reduce overhead
        if (data.type === 'input_audio_buffer.append') {
          console.log(`[WebSocket] 📤 AUDIO: ${data.audio.length} chars`);
        } else {
          // Log detailed info for non-audio messages
          const logData = { ...data };
          if (logData.audio && logData.audio.length > 100) {
            logData.audio = `[${logData.audio.length} chars audio data]`;
          }
          
          console.log(`[WebSocket] 📤 ${data.type}:`, JSON.stringify(logData, null, 2));
        }
        
        this.ws.send(message);
        return true;
      } catch (error) {
        console.error('[WebSocket] ❌ Failed to send:', error);
        return false;
      }
    }
    console.warn('[WebSocket] ⚠️ Cannot send, not connected');
    return false;
  }

  sendAudioChunk(audioData) {
    return this.send({
      type: 'input_audio_buffer.append',
      audio: audioData,
    });
  }

  commitAudio() {
    console.log('[WebSocket] 🎤 Committing audio buffer');
    return this.send({
      type: 'input_audio_buffer.commit',
    });
  }

  requestResponse() {
    console.log('[WebSocket] ⚡ Requesting response');
    return this.send({
      type: 'response.create',
      response: {
        modalities: ['audio', 'text'],
      },
    });
  }

  cancelResponse() {
    console.log('╔═══════════════════════════════════════════════════════╗');
    console.log('║ [WebSocket] ⚡ CANCELLING RESPONSE                   ║');
    console.log('║ - Sending response.cancel to server                 ║');
    console.log('║ - Clearing server-side audio buffer                 ║');
    console.log('║ - Preparing for new user input                      ║');
    console.log('╚═══════════════════════════════════════════════════════╝');
    
    // Cancel the current response
    this.send({
      type: 'response.cancel',
    });
    
    // Also clear any buffered audio on the server
    this.clearAudioBuffer();
  }

  clearAudioBuffer() {
    console.log('[WebSocket] 🗑️ Clearing audio buffer');
    return this.send({
      type: 'input_audio_buffer.clear',
    });
  }

  startKeepalive() {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Keepalive');
      }
    }, 30000);
  }

  stopKeepalive() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log('[WebSocket] Scheduling reconnection:', {
      attempt: this.reconnectAttempts,
      delay,
    });

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    console.log('[WebSocket] Disconnecting');
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopKeepalive();

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }

    this.sessionId = null;
  }
}
